/** Small line icons (currentColor). */
import type { ReactNode } from 'react';

const base = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.1,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function I({ children, size = 22 }: { children: ReactNode; size?: number }) {
  return (
    <svg {...base} width={size} height={size}>
      {children}
    </svg>
  );
}

export const IconMenu = () => (
  <I>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </I>
);
export const IconScores = ({ size }: { size?: number }) => (
  <I {...(size ? { size } : {})}>
    <rect x="4" y="3.5" width="16" height="17" rx="2.5" />
    <path d="M8 8.5h8M8 12.5h8M8 16.5h5" />
  </I>
);
export const IconSound = () => (
  <I>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor" stroke="none" />
    <path d="M15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11" />
  </I>
);
export const IconMute = () => (
  <I>
    <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" fill="currentColor" stroke="none" />
    <path d="M16 9.5l5 5M21 9.5l-5 5" />
  </I>
);
export const IconGear = () => (
  <I>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" />
  </I>
);
export const IconClose = ({ size }: { size?: number }) => (
  <I {...(size ? { size } : {})}>
    <path d="M6 6l12 12M18 6L6 18" />
  </I>
);
export const IconPlus = ({ size }: { size?: number }) => (
  <I {...(size ? { size } : {})}>
    <path d="M12 5v14M5 12h14" />
  </I>
);
export const IconMinus = () => (
  <I>
    <path d="M5 12h14" />
  </I>
);
export const IconLink = () => (
  <I>
    <path d="M10 14a4.5 4.5 0 0 0 6.4 0l2.8-2.8a4.5 4.5 0 0 0-6.4-6.4L11.5 6" />
    <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-2.8 2.8a4.5 4.5 0 0 0 6.4 6.4l1.3-1.3" />
  </I>
);
export const IconHome = () => (
  <I>
    <path d="M4 11l8-6.5 8 6.5M6.5 9.5V20h11V9.5" />
  </I>
);
export const IconDoor = () => (
  <I>
    <path d="M14 4H6v16h8M10 12h10M17 9l3 3-3 3" />
  </I>
);
export const IconOffline = ({ size = 18 }: { size?: number }) => (
  <I size={size}>
    <path d="M2.5 8.5a14 14 0 0 1 5-2.8M21.5 8.5a14 14 0 0 0-8.3-3.4M5.5 12a9 9 0 0 1 4-2M18.5 12a9 9 0 0 0-2-1.3M9 15.5a4.5 4.5 0 0 1 6 0M3 3l18 18" />
    <circle cx="12" cy="19" r="0.8" fill="currentColor" />
  </I>
);
/** A closed eye: "Keep hidden". */
export const IconEyeClosed = ({ size = 20 }: { size?: number }) => (
  <I size={size}>
    <path d="M3 10c2.5 3.5 5.5 5 9 5s6.5-1.5 9-5" />
    <path d="M6 13.5L4.5 16M12 15v3M18 13.5l1.5 2.5" />
  </I>
);
/** Down-arrow into a tray: "toss to the discard pile". */
export const IconToss = ({ size = 18 }: { size?: number }) => (
  <I size={size}>
    <path d="M12 3v10M8 9.5l4 4 4-4" />
    <path d="M4 14v5h16v-5" />
  </I>
);
export const IconSmile = () => (
  <I>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 14a4.5 4.5 0 0 0 7 0" />
    <circle cx="9.2" cy="10" r="0.9" fill="currentColor" stroke="none" />
    <circle cx="14.8" cy="10" r="0.9" fill="currentColor" stroke="none" />
  </I>
);
export const IconChevron = ({ open = false }: { open?: boolean }) => (
  <I size={18}>
    <path d={open ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
  </I>
);
export const IconCopy = () => (
  <I size={18}>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
  </I>
);
export const IconShare = () => (
  <I size={18}>
    <path d="M12 3v12M7.5 7.5L12 3l4.5 4.5" />
    <path d="M5 12v7h14v-7" />
  </I>
);
export const IconPlay = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
  </svg>
);

/** Solid golf flag (went out / hole winner). */
export const Flag = ({ size = 16, outline = false }: { size?: number; outline?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className={`flag-ic${outline ? ' outline' : ''}`}>
    <path d="M6.5 21.5V3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path
      d="M7.2 3.6l11 4.1-11 4.1z"
      fill={outline ? 'none' : 'var(--nn-gold)'}
      stroke={outline ? 'currentColor' : 'var(--nn-gold-dark)'}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
  </svg>
);

/** The Golden Amphora, drawn (fallback for the trophy icon). */
export const AmphoraIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M9 3h6v2.2c0 .8 1 1.3 1 2.8 2.6 1.3 3.4 3.8 3 6.5-.5 3-2.6 5-5 5.5h-4c-2.4-.5-4.5-2.5-5-5.5-.4-2.7.4-5.2 3-6.5 0-1.5 1-2 1-2.8z"
      fill="var(--nn-gold)"
      stroke="var(--nn-ink)"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
    <path d="M8 8.5c-2.5-.5-3.5 1.5-3 3.5M16 8.5c2.5-.5 3.5 1.5 3 3.5" fill="none" stroke="var(--nn-ink)" strokeWidth="1.3" strokeLinecap="round" />
    <rect x="8" y="19.5" width="8" height="2.5" rx="0.6" fill="var(--nn-limestone)" stroke="var(--nn-ink)" strokeWidth="1.2" />
  </svg>
);

/** A laurel sprig: a curved stem with five leaves, flat fill (the hint pill and the hint seal). */
export const IconLaurel = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <g transform="translate(0.4 0.6)">
      <path d="M7.8 22L8 19.4L8.5 16.9L9.3 14.5L10.3 12.2L11.6 9.9L13.2 7.8L15.1 5.7L17.2 3.8L16.8 3.2L14.5 5.1L12.4 7.1L10.7 9.3L9.2 11.6L8 14L7.1 16.5L6.5 19.2L6.2 22Z" />
      <path d="M7.6 17.7Q7.9 13.2 2.9 12Q3.1 17.1 7.6 17.7ZM8.8 13.8Q12.6 15.7 15.4 12Q11 10.2 8.8 13.8ZM10.8 10.1Q12.5 6.6 8.9 4.2Q7.4 8.2 10.8 10.1ZM13.4 6.8Q15.7 9.5 19 7.2Q16.1 4.5 13.4 6.8ZM16.1 4.3Q19.5 4.8 20.2 1Q16.3 0.8 16.1 4.3Z" />
    </g>
  </svg>
);

/** The iOS/Safari Share button: a box with an arrow leaving the top. */
export const IconShareBox = ({ size = 18 }: { size?: number }) => (
  <I size={size}>
    <path d="M12 15V3.5M8 7l4-3.8L16 7M7 10.5H6a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 18 10.5h-1" />
  </I>
);
/** The browser's three-dot menu (vertical). */
export const IconDotsV = ({ size = 18 }: { size?: number }) => (
  <I size={size}>
    <circle cx="12" cy="5.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.5" fill="currentColor" stroke="none" />
  </I>
);
/** The address-bar "install" glyph: a screen with an arrow dropping in. */
export const IconInstallScreen = ({ size = 18 }: { size?: number }) => (
  <I size={size}>
    <path d="M12 4v9M8.5 9.5L12 13l3.5-3.5M4.5 14.5v3A2 2 0 0 0 6.5 19.5h11a2 2 0 0 0 2-2v-3" />
  </I>
);
